import React, { useEffect, useState } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { User, Mail, Phone, Bell, MessageSquare, Save, TestTube } from 'lucide-react';
import axios from 'axios';
import { API, useAuth } from '@/App';
import { toast } from 'sonner';

const SettingsPage = () => {
  const { user, token, refreshUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testingEmail, setTestingEmail] = useState(false);
  const [testingSms, setTestingSms] = useState(false);
  const [preferences, setPreferences] = useState({
    email_enabled: true,
    sms_enabled: false,
    email: '',
    phone: ''
  });

  useEffect(() => {
    const fetchPreferences = async () => {
      try {
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const response = await axios.get(`${API}/notifications/preferences`, {
          headers,
          withCredentials: true
        });
        setPreferences({
          email_enabled: response.data.email_enabled ?? true,
          sms_enabled: response.data.sms_enabled ?? false,
          email: response.data.email || user?.email || '',
          phone: response.data.phone || user?.phone || ''
        });
      } catch (error) {
        console.error('Error fetching preferences:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchPreferences();
  }, [token, user]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.put(`${API}/notifications/preferences`, preferences, {
        headers,
        withCredentials: true
      });
      toast.success('Preferences saved successfully');
    } catch (error) {
      toast.error('Failed to save preferences');
    } finally {
      setSaving(false);
    }
  };

  const handleTestEmail = async () => {
    setTestingEmail(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.post(`${API}/notifications/test-email`, {}, {
        headers,
        withCredentials: true
      });
      toast.success('Test email sent! Check your inbox.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send test email');
    } finally {
      setTestingEmail(false);
    }
  };

  const handleTestSms = async () => {
    setTestingSms(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.post(`${API}/notifications/test-sms`, {}, {
        headers,
        withCredentials: true
      });
      toast.success('Test SMS sent! Check your phone.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send test SMS');
    } finally {
      setTestingSms(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="p-6 lg:p-8 flex items-center justify-center min-h-[calc(100vh-4rem)]">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-ink"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-8" data-testid="settings-page">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-ink font-display">Settings</h1>
          <p className="text-ink-600 mt-1">Manage your account and notification preferences</p>
        </div>

        <div className="max-w-2xl space-y-6">
          {/* Profile Section */}
          <Card className="bg-white border border-ink-200 shadow-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 font-display">
                <User className="w-5 h-5" />
                Profile
              </CardTitle>
              <CardDescription>Your account information</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-4">
                {user?.picture ? (
                  <img src={user.picture} alt={user.name} className="w-16 h-16 rounded-full" />
                ) : (
                  <div className="w-16 h-16 rounded-full bg-cream flex items-center justify-center">
                    <span className="text-2xl font-bold text-ink">
                      {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                    </span>
                  </div>
                )}
                <div>
                  <p className="text-lg font-semibold text-ink">{user?.name}</p>
                  <p className="text-ink-600">{user?.email}</p>
                  <p className="text-sm text-ink-600/70 capitalize mt-1">
                    Plan: {user?.subscription_plan || 'Free'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Notification Preferences */}
          <Card className="bg-white border border-ink-200 shadow-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 font-display">
                <Bell className="w-5 h-5" />
                Notification Preferences
              </CardTitle>
              <CardDescription>Choose how you want to receive renewal reminders</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Email Notifications */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-md bg-cream flex items-center justify-center">
                      <Mail className="w-5 h-5 text-respondent" />
                    </div>
                    <div>
                      <p className="font-medium text-ink">Email Notifications</p>
                      <p className="text-sm text-ink-600">Receive reminders via email</p>
                    </div>
                  </div>
                  <Switch
                    checked={preferences.email_enabled}
                    onCheckedChange={(checked) => setPreferences({ ...preferences, email_enabled: checked })}
                    data-testid="email-notifications-toggle"
                  />
                </div>
                {preferences.email_enabled && (
                  <div className="pl-13 space-y-3">
                    <div className="space-y-2">
                      <Label htmlFor="email" className="text-ink">Email Address</Label>
                      <Input
                        id="email"
                        type="email"
                        value={preferences.email}
                        onChange={(e) => setPreferences({ ...preferences, email: e.target.value })}
                        placeholder="your@email.com"
                        className="border-ink-200"
                        data-testid="notification-email-input"
                      />
                    </div>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={handleTestEmail}
                      disabled={testingEmail || !preferences.email}
                      className="hover:bg-cream hover:text-ink"
                      data-testid="test-email-btn"
                    >
                      <TestTube className="w-4 h-4 mr-2" />
                      {testingEmail ? 'Sending...' : 'Send Test Email'}
                    </Button>
                  </div>
                )}
              </div>

              <Separator />

              {/* SMS Notifications */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-md bg-emerald-50 flex items-center justify-center">
                      <MessageSquare className="w-5 h-5 text-emerald-600" />
                    </div>
                    <div>
                      <p className="font-medium text-ink">SMS Notifications</p>
                      <p className="text-sm text-ink-600">Receive reminders via text message</p>
                    </div>
                  </div>
                  <Switch
                    checked={preferences.sms_enabled}
                    onCheckedChange={(checked) => setPreferences({ ...preferences, sms_enabled: checked })}
                    data-testid="sms-notifications-toggle"
                  />
                </div>
                {preferences.sms_enabled && (
                  <div className="pl-13 space-y-3">
                    <div className="space-y-2">
                      <Label htmlFor="phone" className="text-ink">Phone Number</Label>
                      <Input
                        id="phone"
                        type="tel"
                        value={preferences.phone}
                        onChange={(e) => setPreferences({ ...preferences, phone: e.target.value })}
                        placeholder="+1 (555) 123-4567"
                        className="border-ink-200"
                        data-testid="notification-phone-input"
                      />
                      <p className="text-xs text-ink-600">Use international format (e.g., +14155551234)</p>
                    </div>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={handleTestSms}
                      disabled={testingSms || !preferences.phone}
                      className="hover:bg-cream hover:text-ink"
                      data-testid="test-sms-btn"
                    >
                      <TestTube className="w-4 h-4 mr-2" />
                      {testingSms ? 'Sending...' : 'Send Test SMS'}
                    </Button>
                  </div>
                )}
              </div>

              <Separator />

              {/* Save Button */}
              <Button 
                onClick={handleSave}
                disabled={saving}
                className="bg-ink hover:bg-black text-white"
                data-testid="save-preferences-btn"
              >
                <Save className="w-4 h-4 mr-2" />
                {saving ? 'Saving...' : 'Save Preferences'}
              </Button>
            </CardContent>
          </Card>

          {/* Plan Info */}
          <Card className="bg-white border border-ink-200 shadow-sm">
            <CardHeader>
              <CardTitle className="font-display">Current Plan</CardTitle>
              <CardDescription>Your subscription details</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-lg font-semibold text-ink capitalize">
                    {user?.subscription_plan || 'Free'} Plan
                  </p>
                  <p className="text-sm text-ink-600">
                    {user?.subscription_plan === 'free' 
                      ? 'Up to 3 renewals' 
                      : user?.subscription_plan === 'basic'
                      ? 'Up to 10 renewals'
                      : user?.subscription_plan === 'pro'
                      ? 'Up to 50 renewals'
                      : 'Unlimited renewals'
                    }
                  </p>
                </div>
                <Button 
                  variant="outline"
                  onClick={() => window.location.href = '/billing'}
                  className="hover:bg-cream hover:text-ink"
                >
                  {user?.subscription_plan === 'free' ? 'Upgrade' : 'Manage'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default SettingsPage;
